import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Star, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { companyInfo, services, testimonials } from '../data/mock';
import { useLanguage } from '../contexts/LanguageContext';
import * as LucideIcons from 'lucide-react';

export const Home = () => {
  const { language, t } = useLanguage();
  
  const handleWhatsAppClick = () => {
    window.open(`https://wa.me/${companyInfo.whatsapp}`, '_blank');
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-b from-amber-50 to-white">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              {t.home.hero.title}
              <span className="text-amber-600"> {t.home.hero.titleHighlight}</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              {t.home.hero.tagline}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={handleWhatsAppClick}
                size="lg"
                className="bg-green-600 hover:bg-green-700 text-white text-lg px-8 py-6 transition-colors"
              >
                <Phone className="w-5 h-5 mr-2" />
                {t.home.hero.ctaQuote}
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="text-lg px-8 py-6 border-2 border-amber-600 text-amber-600 hover:bg-amber-50 transition-colors"
              >
                <Link to="/galeria">
                  {t.home.hero.ctaGallery}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-8 mt-16 max-w-2xl mx-auto">
              <div>
                <div className="text-4xl font-bold text-amber-600 mb-2">10+</div>
                <div className="text-sm text-gray-600">{t.home.stats.experience}</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-amber-600 mb-2">500+</div>
                <div className="text-sm text-gray-600">{t.home.stats.projects}</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-amber-600 mb-2">100%</div>
                <div className="text-sm text-gray-600">{t.home.stats.satisfaction}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t.home.servicesSection.title}</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t.home.servicesSection.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => {
              const IconComponent = LucideIcons[service.icon];
              return (
                <Card
                  key={service.id}
                  className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-amber-500"
                >
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-amber-600 transition-colors">
                      <IconComponent className="w-6 h-6 text-amber-600 group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {service.title[language]}
                    </h3>
                    <p className="text-gray-600">{service.description[language]}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Button asChild size="lg" className="bg-amber-600 hover:bg-amber-700 text-white transition-colors">
              <Link to="/servicos">
                {t.common.viewAll}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">{t.home.whyUs.title}</h2>
              <p className="text-lg text-gray-600">
                {t.home.whyUs.subtitle}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {t.home.whyUs.benefits.map((benefit, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Preview */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t.home.testimonialsSection.title}</h2>
            <p className="text-lg text-gray-600">
              {t.home.testimonialsSection.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.slice(0, 3).map((testimonial) => (
              <Card key={testimonial.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-600 mb-4 italic">"{testimonial.text[language]}"</p>
                  <div className="border-t pt-4">
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.location}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button asChild size="lg" variant="outline" className="border-2 border-amber-600 text-amber-600 hover:bg-amber-50 transition-colors">
              <Link to="/depoimentos">
                {t.common.viewAllTestimonials}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-amber-600">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-4">
            {t.home.cta.title}
          </h2>
          <p className="text-xl text-amber-100 mb-8 max-w-2xl mx-auto">
            {t.home.cta.subtitle}
          </p>
          <Button
            onClick={handleWhatsAppClick}
            size="lg"
            className="bg-white text-amber-600 hover:bg-gray-100 text-lg px-8 py-6 transition-colors"
          >
            <Phone className="w-5 h-5 mr-2" />
            {t.home.cta.button}
          </Button>
        </div>
      </section>
    </div>
  );
};